/**
 * Validation functions and error messages for onboarding form fields
 */

/**
 * Validates the name field
 * @param value - The name value to validate
 * @returns True if validation fails, false if valid
 */
export const nameValidation = (value: string): boolean => {
  const regex = /[!@#$%^*(),?"':{}`|<>]/g;
  return !!(
    value?.length < 1 ||
    value?.length > 75 ||
    value?.match(regex) ||
    value !== value?.trim()
  );
};

/**
 * Gets the error message for an invalid name
 * @param value - The name value to check
 * @returns Error message string, empty if valid
 */
export const getNameErrorMessage = (value: string): string => {
  if (value?.length < 1) return 'Name is required';
  if (value !== value?.trim())
    return 'Name cannot have leading or trailing spaces';
  if (value?.length > 75) return 'Name must be less than 75 characters';
  const regex = /[!@#$%^*(),?"':{}`|<>]/g;
  if (value?.match(regex))
    return 'Name cannot contain special characters: !@#$%^*(),?"\':{}`|<>';
  return '';
};

/**
 * Validates the description field
 * @param value - The description value to validate
 * @returns True if validation fails, false if valid
 */
export const descriptionValidation = (value: string): boolean => {
  const regex = /[!@#$%^*(),?"':{}`|<>]/g;
  return !!(
    value?.length > 1000 ||
    (value?.length < 20 && value?.length >= 0) ||
    value?.match(regex) ||
    value !== value?.trim()
  );
};

/**
 * Gets the error message for an invalid description
 * @param value - The description value to check
 * @returns Error message string, empty if valid
 */
export const getDescriptionErrorMessage = (value: string): string => {
  if (value?.length < 20 && value?.length >= 0)
    return 'Description must be at least 20 characters';
  if (value !== value?.trim())
    return 'Description cannot have leading or trailing spaces';
  if (value?.length > 1000)
    return 'Description must be less than 1000 characters';
  const regex = /[!@#$%^*(),?"':{}`|<>]/g;
  if (value?.match(regex))
    return 'Description cannot contain special characters: !@#$%^*(),?"\':{}`|<>';
  return '';
};

/**
 * Validates the identifier field (currently disabled but kept for future use)
 * @param value - The identifier value to validate
 * @returns True if validation fails, false if valid
 */
export const identifierValidation = (value: string): boolean => {
  const validIdentifierRegex = /^[a-z0-9-]*$/;
  return !!(
    value?.length < 1 ||
    value?.length > 63 ||
    !validIdentifierRegex.test(value) ||
    value?.startsWith('-') ||
    value?.endsWith('-')
  );
};

/**
 * Gets the error message for an invalid identifier (currently disabled but kept for future use)
 * @param value - The identifier value to check
 * @returns Error message string, empty if valid
 */
export const getIdentifierErrorMessage = (value: string): string => {
  if (value?.length < 1) return 'Identifier is required';
  if (value?.length > 63) return 'Identifier must be less than 63 characters';
  if (value?.startsWith('-') || value?.endsWith('-'))
    return 'Identifier cannot start or end with dashes';
  const validIdentifierRegex = /^[a-z0-9-]*$/;
  if (!validIdentifierRegex.test(value))
    return 'Identifier can only contain lowercase letters, numbers, and dashes';
  return '';
};

/**
 * Creates a URL-friendly slug from a name (currently disabled but kept for future use)
 * @param name - The name to convert to a slug
 * @returns Slugified version of the name
 */
export const createSlug = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};
